import React from 'react';
import styled from 'styled-components';
import {Swiper, SwiperSlide} from 'swiper/react';
import SwiperCore, {Navigation} from 'swiper';
import 'swiper/swiper-bundle.min.css';

import 'swiper/swiper.min.css';

import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';

import {useTranslations} from "use-intl";

import {FETCH_STATUSES, Nullish} from '@tager/web-core';

import {colors} from '@/constants/theme';
import {useTypedSelector} from '@/store/store';
import {selectProductPostListResource} from '@/store/reducers/pages/product/product';
import Link from '@/components/Link';
import PlainPostCard from '@/components/PlainPostCard';

SwiperCore.use([Navigation]);

type Props = {
  id: Nullish<number>;
};

function Blog({id}: Props) {
  const data = useTypedSelector((state) => {
    if (id) {
      return selectProductPostListResource(state, id);
    }
  });

  const t = useTranslations();

  if (!data?.data || !data.data.length) return null;

  return (
    <Component>
      {data?.status === FETCH_STATUSES.SUCCESS ? (
        <>
          <Header>
            <Label>{t('interesting')}</Label>
            <Nav>
              <Prev className={'prev-post-relation-item'}/>
              <Next className={'next-post-relation-item'}/>
            </Nav>
          </Header>
          <Swiper
            navigation={{
              prevEl: '.prev-post-relation-item',
              nextEl: '.next-post-relation-item',
            }}
            slidesOffsetBefore={15}
            slidesOffsetAfter={15}
            spaceBetween={15}
            slidesPerView={1.25}
            slidesPerGroup={1}
            breakpoints={{
              500: {
                slidesPerView: 2.2,
              },
            }}
          >
            {!!data.data.length
              ? data.data.map((post, index) => (
                <SwiperSlide key={index}>
                  <Link to={'/blog/' + post?.urlAlias}>
                    <PlainPostCard post={post}/>
                  </Link>
                </SwiperSlide>
              ))
              : null}
          </Swiper>
        </>
      ) : null}
    </Component>
  );
}

export default Blog;

const Component = styled.div`
  margin-top: 20px;
`;

const Label = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 22px;
  line-height: 40px;
`;

const Header = styled.div`
  padding: 0 15px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 0 0 46px;
`;

const Prev = styled.div`
  cursor: pointer;
  position: relative;
  height: 22.5px;
  width: 15px;

  &:before {
    position: absolute;
    border-radius: 5px 0 0 5px;
    content: '';
    top: 0;
    right: 0;
    height: 1px;
    width: 16px;
    transform-origin: 100% 0;
    transform: rotate(315deg);
    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:after {
    position: absolute;
    border-radius: 5px 0 0 5px;
    content: '';
    bottom: 0;
    right: 0;
    height: 1px;
    width: 15px;
    transform-origin: 100% 0;
    transform: rotate(45deg);
    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:hover:before,
  &:hover:after {
    background-color: ${colors.black300};
  }
`;
const Next = styled.div`
  cursor: pointer;
  position: relative;
  height: 22.5px;
  width: 15px;

  &:before {
    cursor: pointer;
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    height: 1px;
    width: 16px;
    transform-origin: 0 0;
    transform: rotate(45deg);
    border-radius: 0 5px 5px 0;
    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:after {
    cursor: pointer;
    position: absolute;
    content: '';
    bottom: 0;
    left: 0;
    height: 1px;
    width: 15px;
    transform-origin: 0 0;
    transform: rotate(-45deg);
    border-radius: 0 5px 5px 0;

    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:hover:before,
  &:hover:after {
    background-color: ${colors.black300};
  }
`;
